/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { setTitle } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import type { RestaurantAsset } from "@app/data/api/uploads";
import { uploadsAPI, groupUploadsAPI } from "@app/data/api/uploads";
import {
  AssetDetails,
  type AssetTypeOption,
} from "@app/vue/components/AssetDetails";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

// Known asset collections. `type` is meaningful downstream: the customer app
// reads `reservation_gallery` for the reservation page / OG images, and event
// images use `event_images`. Free-form types still work; they surface as-is.
const KNOWN_COLLECTIONS: { value: string; labelKey: string; icon: string }[] = [
  {
    value: "reservation_gallery",
    labelKey: "settings.assets.col_reservation",
    icon: "restaurant",
  },
  {
    value: "event_images",
    labelKey: "settings.assets.col_events",
    icon: "celebration",
  },
  {
    value: "pass_images",
    labelKey: "settings.assets.col_pass_images",
    icon: "wallet",
  },
  { value: "general", labelKey: "settings.assets.col_general", icon: "folder" },
];

const DEFAULT_TYPE = "general";

const contentType = (asset: RestaurantAsset) =>
  (asset.meta?.content_type as string) || "";

// Manual ordering lives in meta.position; unordered assets fall to the end,
// tie-broken by newest-first so fresh uploads surface without an explicit sort.
const assetPosition = (asset: RestaurantAsset): number => {
  const raw = asset.meta?.position;
  return typeof raw === "number" ? raw : Number.MAX_SAFE_INTEGER;
};

const byCollectionOrder = (a: RestaurantAsset, b: RestaurantAsset): number => {
  const pa = assetPosition(a);
  const pb = assetPosition(b);
  if (pa !== pb) return pa - pb;
  return b.created_at.localeCompare(a.created_at);
};

type AssetsSettingsProps = {
  /** Which owner surface to manage. Defaults to the shop. */
  mode?: "shop" | "group";
};

export const AssetsSettings = defineComponent<AssetsSettingsProps>({
  name: "AssetsSettings",
  props: ["mode"],
  setup(props) {
    const { t } = useLocalization();
    setTitle("Assets");

    // Owner-scoped asset API; ownership is resolved server-side from the session.
    const uploads = props.mode === "group" ? groupUploadsAPI : uploadsAPI;

    const assets = ref<RestaurantAsset[]>([]);
    const loading = ref(true);
    const showModal = ref(false);
    const modalMode = ref<"add" | "view">("add");
    const selectedAsset = ref<RestaurantAsset | null>(null);
    const deleteConfirmId = ref<string | null>(null);
    const deleteError = ref("");

    // Filters
    const collectionFilter = ref<string>("");

    // Drag-to-reorder state (scoped to a single collection at a time).
    const draggingId = ref<string | null>(null);
    const draggingType = ref<string | null>(null);

    const loadAssets = async () => {
      try {
        loading.value = true;
        // No type filter -> all assets for the restaurant.
        const result = await uploads.getAssets({});
        assets.value = result;
      } catch (error) {
        console.error("Failed to load assets:", error);
      } finally {
        loading.value = false;
      }
    };

    const collectionMeta = (value: string) =>
      KNOWN_COLLECTIONS.find((c) => c.value === value);

    const collectionLabel = (value: string) => {
      const known = collectionMeta(value);
      if (known) return t(known.labelKey);
      return value
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    };

    const collectionIcon = (value: string) =>
      collectionMeta(value)?.icon ?? "folder";

    const typeOptions = computed<AssetTypeOption[]>(() => {
      const opts = KNOWN_COLLECTIONS.map((c) => ({
        value: c.value,
        label: t(c.labelKey),
      }));
      // Surface any free-form collections already in use so a moved asset can
      // return to them from the details sheet.
      for (const a of assets.value) {
        if (!opts.some((o) => o.value === a.type)) {
          opts.push({ value: a.type, label: collectionLabel(a.type) });
        }
      }
      return opts;
    });

    // Assets grouped into collections, each internally sorted by manual order.
    // Known collections lead in their declared order; free-form types follow.
    const collections = computed(() => {
      const map = new Map<string, RestaurantAsset[]>();
      for (const a of assets.value) {
        if (collectionFilter.value && a.type !== collectionFilter.value)
          continue;
        const bucket = map.get(a.type);
        if (bucket) bucket.push(a);
        else map.set(a.type, [a]);
      }

      const orderedTypes = [
        ...KNOWN_COLLECTIONS.map((c) => c.value).filter((v) => map.has(v)),
        ...Array.from(map.keys())
          .filter((v) => !KNOWN_COLLECTIONS.some((c) => c.value === v))
          .sort(),
      ];

      return orderedTypes.map((type) => ({
        type,
        label: collectionLabel(type),
        icon: collectionIcon(type),
        assets: (map.get(type) ?? []).slice().sort(byCollectionOrder),
      }));
    });

    // Chips: every collection present in the library plus the known ones, so an
    // empty known collection is still reachable as a filter.
    const collectionChips = computed(() => {
      const present = new Set(assets.value.map((a) => a.type));
      const types = [
        ...KNOWN_COLLECTIONS.map((c) => c.value),
        ...Array.from(present)
          .filter((v) => !KNOWN_COLLECTIONS.some((c) => c.value === v))
          .sort(),
      ];
      return types.map((value) => ({
        value,
        label: collectionLabel(value),
        icon: collectionIcon(value),
        count: assets.value.filter((a) => a.type === value).length,
      }));
    });

    // Each collection section always shows the full collection, so reordering
    // (which rewrites position across the whole collection) is always safe.
    const reorderEnabled = computed(() => true);

    const openAddModal = () => {
      modalMode.value = "add";
      selectedAsset.value = null;
      showModal.value = true;
    };

    const openViewModal = (asset: RestaurantAsset) => {
      modalMode.value = "view";
      selectedAsset.value = asset;
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      selectedAsset.value = null;
    };

    const handleSave = async () => {
      await loadAssets();
      closeModal();
    };

    const handleDeleteClick = (asset: RestaurantAsset, event: Event) => {
      event.stopPropagation();
      deleteConfirmId.value = asset.id;
    };

    const confirmDelete = async () => {
      if (!deleteConfirmId.value) return;
      try {
        deleteError.value = "";
        await uploads.deleteAsset(deleteConfirmId.value);
        assets.value = assets.value.filter(
          (a) => a.id !== deleteConfirmId.value,
        );
        deleteConfirmId.value = null;
      } catch (err) {
        console.error("Failed to delete asset:", err);
        deleteError.value =
          err instanceof Error
            ? err.message
            : t("settings.assets.delete_error");
      }
    };

    const formatDate = (dateStr: string) =>
      new Date(dateStr).toLocaleDateString();

    // ── Drag-to-reorder ──────────────────────────────────────────────────────

    const onDragStart = (asset: RestaurantAsset) => {
      if (!reorderEnabled.value) return;
      draggingId.value = asset.id;
      draggingType.value = asset.type;
    };

    const onDragOver = (e: DragEvent, target: RestaurantAsset) => {
      if (!draggingId.value || draggingType.value !== target.type) return;
      e.preventDefault();
      if (draggingId.value === target.id) return;

      const group = assets.value
        .filter((a) => a.type === target.type)
        .sort(byCollectionOrder);
      const from = group.findIndex((a) => a.id === draggingId.value);
      const to = group.findIndex((a) => a.id === target.id);
      if (from < 0 || to < 0) return;

      const reordered = [...group];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);

      // Reflect the new order immediately by stamping local positions; the sorted
      // computed views recompute from these.
      const posById = new Map(reordered.map((a, i) => [a.id, i]));
      assets.value = assets.value.map((a) =>
        posById.has(a.id)
          ? { ...a, meta: { ...a.meta, position: posById.get(a.id) } }
          : a,
      );
    };

    const onDragEnd = async () => {
      const type = draggingType.value;
      draggingId.value = null;
      draggingType.value = null;
      if (!type) return;
      const orderedIds = assets.value
        .filter((a) => a.type === type)
        .sort(byCollectionOrder)
        .map((a) => a.id);
      try {
        await uploads.reorderAssets(type, orderedIds);
      } catch (err) {
        console.error("Failed to persist order:", err);
        // Re-sync from the server so the UI never diverges from stored order.
        await loadAssets();
      }
    };

    onMounted(loadAssets);

    const renderCard = (asset: RestaurantAsset) => (
      <div
        key={asset.id}
        draggable={reorderEnabled.value}
        onDragstart={() => onDragStart(asset)}
        onDragover={(e: DragEvent) => onDragOver(e, asset)}
        onDragend={onDragEnd}
        class={`group relative overflow-hidden rounded-xl border  bg-base-100 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-primary/40 ${
          draggingId.value === asset.id ? "opacity-40 ring-2 ring-primary" : ""
        } ${reorderEnabled.value ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
        onClick={() => openViewModal(asset)}
      >
        {/* Thumbnail */}
        <div class="aspect-square w-full bg-base-200">
          {contentType(asset).startsWith("image/") ? (
            <img
              src={asset.url}
              alt="Asset"
              class="h-full w-full object-cover"
              draggable={false}
            />
          ) : contentType(asset).startsWith("video/") ? (
            <div class="flex h-full w-full items-center justify-center bg-base-300">
              <span class="material-symbols-outlined text-5xl text-base-content/50">
                play_circle
              </span>
            </div>
          ) : (
            <div class="flex h-full w-full items-center justify-center bg-base-300">
              <span class="material-symbols-outlined text-5xl text-base-content/50">
                insert_drive_file
              </span>
            </div>
          )}
        </div>

        {/* Drag affordance */}
        {reorderEnabled.value && (
          <span class="material-symbols-outlined pointer-events-none absolute left-2 top-2 rounded-md bg-base-100/80 p-0.5 text-base text-base-content/60 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            drag_indicator
          </span>
        )}

        {/* Delete */}
        <button
          class="btn btn-circle btn-xs btn-error absolute right-2 top-2 z-10 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={(e: Event) => handleDeleteClick(asset, e)}
          title={t("settings.assets.delete_asset")}
        >
          <span class="material-symbols-outlined text-sm">delete</span>
        </button>

        {/* Hover overlay */}
        <div class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
          <span class="material-symbols-outlined text-3xl text-white drop-shadow">
            edit
          </span>
        </div>
      </div>
    );

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="perm_media"
          title={t("settings.assets.page_title")}
          description={t("settings.assets.page_description")}
        >
          {{
            actions: () => (
              <button
                type="button"
                class="btn btn-primary btn-sm"
                onClick={openAddModal}
              >
                <span class="material-symbols-outlined text-sm">add</span>
                {t("settings.assets.add_button")}
              </button>
            ),
            bottom: () => (
              <div class="flex flex-col gap-3 border-t py-3">
                {/* Collection chips */}
                <div class="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    class={`btn btn-xs gap-1 ${
                      collectionFilter.value === ""
                        ? "btn-primary"
                        : "btn-ghost bg-base-200"
                    }`}
                    onClick={() => (collectionFilter.value = "")}
                  >
                    {t("settings.assets.all_collections")}
                  </button>
                  {collectionChips.value.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      class={`btn btn-xs gap-1 ${
                        collectionFilter.value === chip.value
                          ? "btn-primary"
                          : "btn-ghost bg-base-200"
                      }`}
                      onClick={() =>
                        (collectionFilter.value =
                          collectionFilter.value === chip.value
                            ? ""
                            : chip.value)
                      }
                    >
                      <span class="material-symbols-outlined text-sm">
                        {chip.icon}
                      </span>
                      {chip.label}
                      <span class="opacity-60">{chip.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            {loading.value ? (
              <div class="flex items-center justify-center py-12">
                <span class="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : collections.value.length === 0 ? (
              <div class="rounded-lg bg-base-200 py-12 text-center">
                <span class="material-symbols-outlined mb-3 text-5xl text-base-content/40">
                  perm_media
                </span>
                <p>{t("settings.assets.empty_title")}</p>
                <p class="mt-1 text-sm text-base-content/60">
                  {t("settings.assets.empty_desc")}
                </p>
                <button
                  type="button"
                  class="btn btn-primary btn-sm mt-4"
                  onClick={openAddModal}
                >
                  {t("settings.assets.upload_first_button")}
                </button>
              </div>
            ) : (
              <div class="space-y-8">
                {collections.value.map((collection) => (
                  <section key={collection.type} class="space-y-3">
                    {/* Collection header */}
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-primary">
                        {collection.icon}
                      </span>
                      <h2 class="text-base font-semibold text-base-content">
                        {collection.label}
                      </h2>
                      <span class="badge badge-ghost badge-sm">
                        {collection.assets.length}
                      </span>
                      {reorderEnabled.value && collection.assets.length > 1 && (
                        <span class="ml-auto text-xs text-base-content/45">
                          {t("settings.assets.reorder_hint")}
                        </span>
                      )}
                    </div>

                    {/* Asset grid */}
                    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {collection.assets.map(renderCard)}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </AppSubpageContent>
        </AppSubpage>

        {/* Asset Details Sheet */}
        {showModal.value ? (
          <AssetDetails
            mode={modalMode.value}
            asset={selectedAsset.value}
            assetType={collectionFilter.value || DEFAULT_TYPE}
            typeOptions={typeOptions.value}
            isOpen={showModal.value}
            uploadsApi={uploads}
            onClose={closeModal}
            onSave={handleSave}
          />
        ) : null}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId.value && (
          <div class="modal modal-open">
            <div class="modal-box">
              <h3 class="mb-4 text-lg font-bold text-accent">
                {t("settings.assets.confirm_delete_title")}
              </h3>
              <p class="mb-6">{t("settings.assets.confirm_delete_desc")}</p>
              {deleteError.value && (
                <div class="alert alert-error mb-4">
                  <span class="material-symbols-outlined">error</span>
                  <span>{deleteError.value}</span>
                </div>
              )}
              <div class="flex justify-end gap-3">
                <button
                  class="btn btn-ghost"
                  onClick={() => {
                    deleteConfirmId.value = null;
                    deleteError.value = "";
                  }}
                >
                  {t("settings.assets.cancel_button")}
                </button>
                <button class="btn btn-error" onClick={confirmDelete}>
                  <span class="material-symbols-outlined">delete</span>
                  {t("settings.assets.delete_button")}
                </button>
              </div>
            </div>
            <div
              class="modal-backdrop"
              onClick={() => {
                deleteConfirmId.value = null;
                deleteError.value = "";
              }}
            />
          </div>
        )}
      </div>
    );
  },
});
